import type {TFunction} from "i18next";
import {AutomationNode, NodeConnection} from "crqlar-react-common/dist/models/automation/automation";

import {stepName} from "./nodeCategories";
import {getNodeOutputs, NodeOutput} from "./nodeOutputs";

/**
 * IF_CONDITION rules: `conditionGroups` is an AND/OR rule tree (the audience builder's model) the engine evaluates.
 * A node still holding the legacy flat `conditions` reads through foldLegacyConditions; it is rewritten only on edit.
 */

export type IfLogicOperator = 'AND' | 'OR';

export interface IfConditionRule {
    /** A variable reference, `{{"Node label".fieldName}}`. */
    field: string;
    operator: string;
    value?: any;
}

export interface IfConditionGroup {
    operator: IfLogicOperator;
    rules?: IfConditionRule[];
    groups?: IfConditionGroup[];
}

/** A legacy row: the operator on a row joins it to the row *after* it. */
interface LegacyCondition {
    field?: string;
    operator?: string;
    value?: any;
    logicOperator?: IfLogicOperator;
}

export const TODAY_VALUE = '{{today}}';

/** Operators that compare nothing — the value column is not offered for them. */
export const NO_VALUE_OPERATORS = ['exists', 'not_exists', 'contact_not_received'];

/** Operator values are stored on nodes — never rename one. Labels are translation keys. */
export const OPERATORS_BY_TYPE: Record<string, Array<{value: string; label: string}>> = {
    contactHistory: [
        {value: 'contact_not_received_in_days', label: 'Not received in the last'},
        {value: 'contact_not_received', label: 'Never received'},
        {value: 'contact_fewer_than', label: 'Received fewer than'},
        {value: 'contact_fewer_than_in_days', label: 'Received fewer than in a period'},
    ],
    reservationContactHistory: [
        {value: 'contact_fewer_than', label: 'Received fewer than'},
        {value: 'contact_not_received', label: 'Never received'},
        {value: 'contact_not_received_in_days', label: 'Not received in the last'},
        {value: 'contact_fewer_than_in_days', label: 'Received fewer than in a period'},
    ],
    string: [
        {value: 'equals', label: 'Equals'},
        {value: 'not_equals', label: 'Not Equals'},
        {value: 'contains', label: 'Contains'},
        {value: 'not_contains', label: 'Does Not Contain'},
        {value: 'starts_with', label: 'Starts With'},
        {value: 'ends_with', label: 'Ends With'},
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'}
    ],
    number: [
        {value: 'equals', label: 'Equals'},
        {value: 'not_equals', label: 'Not Equals'},
        {value: 'greater_than', label: 'Greater Than'},
        {value: 'less_than', label: 'Less Than'},
        {value: 'greater_or_equal', label: 'Greater Than or Equal'},
        {value: 'less_or_equal', label: 'Less Than or Equal'},
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'}
    ],
    boolean: [
        {value: 'equals', label: 'Equals'},
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'}
    ],
    date: [
        {value: 'equals', label: 'Equals'},
        {value: 'not_equals', label: 'Not Equals'},
        {value: 'before', label: 'Before'},
        {value: 'after', label: 'After'},
        {value: 'older_than_days', label: 'At least N days ago'},
        {value: 'within_last_days', label: 'Within the last N days'},
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'}
    ],
    object: [
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'}
    ],
    array: [
        {value: 'exists', label: 'Exists'},
        {value: 'not_exists', label: 'Does Not Exist'},
        {value: 'contains', label: 'Contains'},
        {value: 'not_contains', label: 'Does Not Contain'}
    ]
};

export const operatorsForType = (fieldType: string) => OPERATORS_BY_TYPE[fieldType] || OPERATORS_BY_TYPE.string;

export const operatorLabel = (operator: string, t: TFunction): string => {
    const known = Object.values(OPERATORS_BY_TYPE).flat().find(entry => entry.value === operator);
    return known ? t(known.label) : operator;
};

export interface IfFieldOption {
    /** What is stored: `{{"Node label".fieldName}}`. */
    value: string;
    label: string;
    /** The node the value comes from — the picker's group heading. */
    group: string;
    type: string;
    valueKind?: NodeOutput['valueKind'];
    /** A closed set of values — an equality rule on the field picks from them rather than typing. */
    options?: NodeOutput['options'];
    description?: string;
}

export const fieldReference = (nodeName: string, fieldName: string): string => `{{"${nodeName}".${fieldName}}}`;

/** Every output of the upstream steps as picker options, grouped by producing step. */
export const buildFieldOptions = (upstreamNodes: AutomationNode[], t: TFunction): IfFieldOption[] =>
    upstreamNodes.flatMap(node => {
        const nodeName = stepName(node);

        return getNodeOutputs(node.type).map(output => ({
            value: fieldReference(nodeName, output.name),
            label: output.label ? t(output.label) : output.name,
            group: nodeName,
            type: output.type,
            valueKind: output.valueKind,
            options: output.options,
            description: output.description
        }));
    });

/** Equality is case-sensitive in the executor. Preserve the stored code's case when choosing
 * another language/country, and its exact value when reselecting the same one. */
export const codeComparisonValue = (code: string, currentValue: string): string => {
    if (code.toLowerCase() === currentValue.toLowerCase()) return currentValue;
    return /^[a-z]{2}$/i.test(code) && /^[A-Z]{2}$/.test(currentValue) ? code.toUpperCase() : code;
};

export const fieldTypeOf = (field: string, options: IfFieldOption[]): string => {
    if (!field) return 'string';
    const known = options.find(option => option.value === field);
    return known?.type || 'string';
};

export const emptyRule = (): IfConditionRule => ({field: '', operator: ''});

export const newBox = (): IfConditionGroup => ({operator: 'AND', rules: [emptyRule()]});

export const groupMembers = (group: IfConditionGroup): number =>
    (group.rules?.length ?? 0) + (group.groups?.length ?? 0);

export const groupIsEmpty = (group: IfConditionGroup): boolean => groupMembers(group) === 0;

export const groupHasRules = (group: IfConditionGroup): boolean =>
    (group.rules?.length ?? 0) > 0 || (group.groups ?? []).some(groupHasRules);

/** A rule the engine can actually evaluate: it needs a field and an operator. */
export const isRuleComplete = (rule: IfConditionRule): boolean => !!rule.field && !!rule.operator;

export const countRules = (group: IfConditionGroup): number =>
    (group.rules?.length ?? 0) + (group.groups ?? []).reduce((total, sub) => total + countRules(sub), 0);

export const countIncompleteRules = (group: IfConditionGroup): number =>
    (group.rules ?? []).filter(rule => !isRuleComplete(rule)).length
    + (group.groups ?? []).reduce((total, sub) => total + countIncompleteRules(sub), 0);

/**
 * The legacy flat list as the tree `IfConditionExecutor` evaluated: left to right, no precedence, so
 * `A AND B OR C AND D` becomes `AND[ OR[ AND[A, B], C ], D ]`. Untouched nodes must keep matching the same.
 */
export const foldLegacyConditions = (conditions: LegacyCondition[]): IfConditionGroup[] => {
    const usable = conditions.filter(condition => !!condition && (!!condition.field || !!condition.operator));
    if (usable.length === 0) return [];

    const asRule = (condition: LegacyCondition): IfConditionRule => ({
        field: condition.field || '',
        operator: condition.operator || '',
        value: condition.value
    });

    if (usable.length === 1) return [{operator: 'AND', rules: [asRule(usable[0])]}];

    // A row's logicOperator joins it to the row after it.
    let accumulated: IfConditionGroup = {operator: 'AND', rules: [asRule(usable[0])]};

    for (let index = 1; index < usable.length; index++) {
        const joinWith: IfLogicOperator = usable[index - 1].logicOperator === 'OR' ? 'OR' : 'AND';
        const rule = asRule(usable[index]);

        if (accumulated.operator === joinWith) {
            accumulated = {...accumulated, rules: [...(accumulated.rules ?? []), rule]};
        } else {
            accumulated = {operator: joinWith, rules: [rule], groups: [accumulated]};
        }
    }

    // The builder's top level is a list of OR boxes, so an OR root spreads into one box per operand.
    if (accumulated.operator === 'OR') {
        const operands: IfConditionGroup[] = [
            ...(accumulated.groups ?? []),
            ...(accumulated.rules ?? []).map(rule => ({operator: 'AND' as IfLogicOperator, rules: [rule]}))
        ];

        return operands.map(operand => (
            operand.operator === 'AND' ? operand : {operator: 'AND', rules: [], groups: [operand]}
        ));
    }

    return [accumulated];
};

/** A non-empty `conditionGroups` wins; otherwise the legacy list is folded. Reading never writes back. */
export const readConditionGroups = (config?: Record<string, any> | null): IfConditionGroup[] => {
    const stored = config?.conditionGroups;
    if (Array.isArray(stored) && stored.length > 0) return stored as IfConditionGroup[];

    const legacy = config?.conditions;
    return Array.isArray(legacy) ? foldLegacyConditions(legacy as LegacyCondition[]) : [];
};

/** Always drops the legacy `conditions` key in the same write. */
export const writeConditionGroups = (groups: IfConditionGroup[]): Record<string, any> => ({
    conditionGroups: groups,
    conditions: undefined
});

export interface BranchSteps {
    /** Labels of the steps directly on the handle, in flow order. */
    yes: string[];
    no: string[];
}

/** Only the steps directly on each handle, not the whole branch — the summary says where the run goes next. */
export const branchSteps = (
    nodeId: string | undefined,
    nodes: AutomationNode[],
    connections: NodeConnection[]
): BranchSteps => {
    if (!nodeId) return {yes: [], no: []};

    const labelOf = (targetId: string): string | undefined => {
        const target = nodes.find(node => node.id === targetId);
        return target && stepName(target);
    };

    const onHandle = (handle: string): string[] => connections
        .filter(connection => connection.sourceNodeId === nodeId && (connection.sourceHandle ?? 'true') === handle)
        .map(connection => labelOf(connection.targetNodeId))
        .filter((label): label is string => !!label);

    return {yes: onHandle('true'), no: onHandle('false')};
};

/** "Thank you (German)", or "Thank you (German) and Wait 3 days" for a fork on the same handle. */
export const formatSteps = (steps: string[], t: TFunction): string => {
    if (steps.length === 0) return '';
    if (steps.length === 1) return steps[0];
    return `${steps.slice(0, -1).join(', ')} ${t('and')} ${steps[steps.length - 1]}`;
};
